import React, {useEffect, useRef, useImperativeHandle, forwardRef} from 'react';
import videojs from 'video.js';
import 'video.js/dist/video-js.css';
import 'videojs-contrib-ads';
import 'videojs-contrib-ads/src/plugin.scss';
import videojsPlaylistPlugin from 'videojs-playlist';
import 'videojs-ima';
import 'videojs-ima/src/css/videojs.ima.css';

videojs.registerPlugin('playlist', videojsPlaylistPlugin);

const defaultVideojsOptions = {
    controls: true,
    autoplay: false,
    fluid: true,
    preload: 'auto'
};

/**
 * Video.js player react component.
 *
 * One common prop is `sources`. `sources` is a list of dictionaries with `src` and `type` keys
 * used to specify the video file to be played, see https://docs.videojs.com/tutorial-options.html#sources
 * for more information.
 *
 * Another useful option is providing the prop `poster`, when provided it is used as the cover image for the
 * player when the video is not playing.
 *
 * Can pass the component a `ref` to access the internal state such as the video element (`ref.current.elementRef`) and
 * the video.js player (`ref.current.playerRef`).
 *
 * @param videojsClasses - The classes to add to the video element, if not provided defaults to
 *  `video-js vjs-big-play-centered`.
 * @param videojsOptions - All of the video.js options can be passed as attributes on the component.
 *  We have defaults for the basic options, look in the code to see what we set if no options are passed or
 *  if an option is not provided.
 *  See https://docs.videojs.com/tutorial-options.html for all the options.
 * @returns {*}
 * @constructor
 */
const VideoPlayer = forwardRef(({videojsClasses = '', dfpTag, ...videojsOptions}, ref) => {
    const videoEle = useRef(null);
    const videoJsInstance = useRef(null);

    // Instantiate videojs for the video element
    useEffect(() => {
        let options = {...defaultVideojsOptions, ...videojsOptions};
        videoJsInstance.current = videojs(videoEle.current, options);
        if (dfpTag) {
            videoJsInstance.current.ima({
                adTagUrl: dfpTag
            })
        }
        // Cleanup the video player
        return () => {
            videoJsInstance.current.dispose();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [videoEle]);

    // Expose the video element and the video.js instance to parent components
    useImperativeHandle(ref, () => ({
        elementRef: videoEle,
        playerRef: videoJsInstance
    }), [videoEle, videoJsInstance]);

    return (
        <div data-vjs-player>
            <video
                ref={videoEle}
                className={videojsClasses !== '' ? videojsClasses : 'video-js vjs-big-play-centered'}
            >
            </video>
        </div>
    );
});
VideoPlayer.displayName = "VideoPlayer";

export default VideoPlayer;
